import React, { useEffect, useState } from 'react';
import {
    Drawer, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, Divider, IconButton, TextField, Button, Stack, Alert, Checkbox, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LeaveAccrualService } from '../../services/leaveAccrual.service';
import { LeaveAccrualHistory, LeaveAccrualSource } from '../../interfaces/leaveAccrual.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

interface Props {
    open: boolean;
    onClose: () => void;
    userId: string;
    userName: string;
    leaveTypeId: string;
    leaveTypeName: string;
    // Fix round 1 / F5: run/carry-forward/undo change `allocated` for EVERY row of the balance
    // grid this drawer is opened from (spec §2.4 path 2), not just this tuple — the caller
    // should pass its own refetch so the grid doesn't go stale once the drawer closes.
    onMutated?: () => void;
}

const SOURCE_LABELS: Record<LeaveAccrualSource, string> = {
    SCHEDULED: 'Scheduled',
    BACKFILL: 'Backfill',
    CARRY_FORWARD: 'Carry forward',
};

// Spec §2.3 validation bounds (task-5-report: @Min/@Max on year/month/closingFiscalYear,
// answered as a generic 400 "Invalid request parameter" if violated) — mirrored client-side so
// an out-of-range value is refused locally with a visible reason instead of round-tripping.
const YEAR_MIN = 2020;
const YEAR_MAX = 2100;
const MONTH_MIN = 1;
const MONTH_MAX = 12;
const isValidYear = (y: number) => Number.isInteger(y) && y >= YEAR_MIN && y <= YEAR_MAX;
const isValidMonth = (m: number) => Number.isInteger(m) && m >= MONTH_MIN && m <= MONTH_MAX;
const pad2 = (n: number) => String(n).padStart(2, '0');

// Spec §3 / task-6-brief: per-user "Accrual history" drawer. referenceDate/tenureGateOpen/
// joiningDate/anniversaryDate come straight off LeaveAccrualHistoryDTO (task-5-report). rows key
// their "Carried" label off periodMonth === 0 alone — there is no server-side boolean for it.
const AccrualHistoryDrawer: React.FC<Props> = ({
    open, onClose, userId, userName, leaveTypeId, leaveTypeName, onMutated,
}) => {
    const [history, setHistory] = useState<LeaveAccrualHistory | null>(null);
    const [loading, setLoading] = useState(false);
    const [historyError, setHistoryError] = useState(false);
    // Fix round 1 / F7: lazy initialisers so "now" is read once per mount, not once per module
    // import — a long-lived SPA session that crosses a month boundary no longer defaults stale.
    const [backfillYear, setBackfillYear] = useState<number>(() => new Date().getFullYear());
    const [backfillMonth, setBackfillMonth] = useState<number>(() => new Date().getMonth() + 1);
    const [closingFiscalYear, setClosingFiscalYear] = useState<number>(() => new Date().getFullYear());
    const [force, setForce] = useState(false);
    const [actionPending, setActionPending] = useState(false);
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.LEAVE_ALLOCATION_MANAGE);

    const fetchHistory = async () => {
        setLoading(true);
        setHistoryError(false);
        try {
            const data = await LeaveAccrualService.history(userId, leaveTypeId);
            setHistory(data);
        } catch (err) {
            setHistoryError(true);
            toast.showApiError(err, 'Failed to load accrual history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && userId && leaveTypeId) {
            fetchHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userId, leaveTypeId]);

    const afterMutation = async () => {
        await fetchHistory();
        onMutated?.();
    };

    const handleBackfill = async () => {
        if (!isValidYear(backfillYear) || !isValidMonth(backfillMonth)) return;
        setActionPending(true);
        try {
            await LeaveAccrualService.run(backfillYear, backfillMonth);
            toast.showSuccess(
                `Accrual run complete for the institution (${backfillYear}-${pad2(backfillMonth)})`
            );
            await afterMutation();
        } catch (err) {
            toast.showApiError(err, 'Failed to run accrual');
        } finally {
            setActionPending(false);
        }
    };

    const handleCarryForward = async () => {
        if (!isValidYear(closingFiscalYear)) return;
        setActionPending(true);
        try {
            await LeaveAccrualService.carryForward(closingFiscalYear, force);
            // Fix round 1 / F6: a 204 here can also mean the server SKIPPED a no-op (spec §2.1
            // [M3], live row already exists and force was not set) — never claim "complete"
            // without a response body that says so.
            toast.showSuccess(
                `Carry-forward request accepted for the institution (FY ${closingFiscalYear})`
            );
            await afterMutation();
        } catch (err) {
            toast.showApiError(err, 'Failed to carry forward');
        } finally {
            setActionPending(false);
        }
    };

    const handleUndo = async () => {
        if (!isValidYear(closingFiscalYear)) return;
        // Fix round 1 / F2: irreversible, institution-wide soft-delete — confirm the blast
        // radius explicitly, mirroring LeaveTypeManagement.tsx's handleToggleActive confirm.
        if (!window.confirm(
            `Undo the carry-forward for fiscal year ${closingFiscalYear}? This deletes the `
            + 'carried-forward balances for EVERY employee in this institution.'
        )) {
            return;
        }
        setActionPending(true);
        try {
            await LeaveAccrualService.undoCarryForward(closingFiscalYear);
            toast.showSuccess(`Carry-forward undone for the institution (FY ${closingFiscalYear})`);
            await afterMutation();
        } catch (err) {
            toast.showApiError(err, 'Failed to undo carry-forward');
        } finally {
            setActionPending(false);
        }
    };

    const renderGateHeader = () => {
        if (!history) return null;
        if (!history.joiningDate) {
            return <Typography variant="body2" color="text.secondary">No joining date recorded</Typography>;
        }
        if (history.tenureGateOpen) {
            return (
                <Typography variant="body2" color="success.main">
                    Tenure gate open — anniversary {history.anniversaryDate}
                </Typography>
            );
        }
        return (
            <Typography variant="body2" color="warning.main">
                Spendable from {history.anniversaryDate}
            </Typography>
        );
    };

    const yearError = !isValidYear(backfillYear);
    const monthError = !isValidMonth(backfillMonth);
    const closingFyError = !isValidYear(closingFiscalYear);

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'leave.accrual.page' } as any}>
            <Box sx={{ width: 480, p: 3 }} role="presentation">
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Accrual history — {userName}</Typography>
                    <IconButton data-help="leave.accrual.close" onClick={onClose} aria-label="Close accrual history">
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Typography variant="body2" color="text.secondary">{leaveTypeName}</Typography>

                {loading && <Typography sx={{ mt: 2 }}>Loading...</Typography>}

                {!loading && historyError && !history && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2, mb: 2 }}
                        action={<Button data-help="leave.accrual.retry" color="inherit" size="small" onClick={fetchHistory}>Retry</Button>}
                    >
                        Failed to load accrual history.
                    </Alert>
                )}

                {!loading && history && (
                    <>
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="body2">Reference date: {history.referenceDate}</Typography>
                            {renderGateHeader()}
                        </Box>

                        <TableContainer data-help-section="leave.accrual.table" component={Paper} sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="leave.accrual.col.month">Month</TableCell>
                                        <TableCell data-help="leave.accrual.col.worked" align="right">Worked</TableCell>
                                        <TableCell data-help="leave.accrual.col.carryIn" align="right">Carry-in</TableCell>
                                        <TableCell data-help="leave.accrual.col.accrued" align="right">Accrued</TableCell>
                                        <TableCell data-help="leave.accrual.col.remainder" align="right">Remainder</TableCell>
                                        <TableCell data-help="leave.accrual.col.source">Source</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.rows.map((row, idx) => (
                                        <TableRow key={`${row.fiscalYear}-${row.periodYear}-${row.periodMonth}-${idx}`}>
                                            <TableCell>
                                                {row.periodMonth === 0
                                                    ? 'Carried'
                                                    : `${row.periodYear}-${pad2(row.periodMonth)}`}
                                            </TableCell>
                                            <TableCell align="right">{row.daysWorked}</TableCell>
                                            <TableCell align="right">{row.carryIn}</TableCell>
                                            <TableCell align="right">{row.accrued}</TableCell>
                                            <TableCell align="right">{row.carryRemainder}</TableCell>
                                            <TableCell>
                                                <Chip label={SOURCE_LABELS[row.source]} size="small" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {history.rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">No accrual rows yet</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {/* Fix round 1 / F8: hoisted out from under the `history &&` guard — a transient
                    history-load failure must not also hide the write controls with no recourse. */}
                {canManage && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2">Institution-wide accrual actions</Typography>
                        {/* Fix round 1 / F1: these three routes are institutionId-scoped only
                            (task-5-report) — they touch every member and every accrual-enabled
                            leave type, not just the user this drawer is titled for. */}
                        <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                            These run for every employee and every accrual-enabled leave type in
                            this institution — not just {userName}.
                        </Alert>

                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
                            <TextField
                                data-help="leave.accrual.year"
                                label="Year"
                                type="number"
                                size="small"
                                value={backfillYear}
                                onChange={(e) => setBackfillYear(Number(e.target.value))}
                                inputProps={{ min: YEAR_MIN, max: YEAR_MAX }}
                                error={yearError}
                                helperText={yearError ? `${YEAR_MIN}-${YEAR_MAX}` : ' '}
                                sx={{ width: 120 }}
                            />
                            <TextField
                                data-help="leave.accrual.month"
                                label="Month"
                                type="number"
                                size="small"
                                value={backfillMonth}
                                onChange={(e) => setBackfillMonth(Number(e.target.value))}
                                inputProps={{ min: MONTH_MIN, max: MONTH_MAX }}
                                error={monthError}
                                helperText={monthError ? '1-12' : ' '}
                                sx={{ width: 100 }}
                            />
                            <Button data-help="leave.accrual.action.backfill"
                                variant="outlined"
                                size="small"
                                onClick={handleBackfill}
                                disabled={actionPending || yearError || monthError}
                            >
                                Backfill month for all employees
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                            <TextField
                                data-help="leave.accrual.closingYear"
                                label="Closing fiscal year"
                                type="number"
                                size="small"
                                value={closingFiscalYear}
                                onChange={(e) => setClosingFiscalYear(Number(e.target.value))}
                                inputProps={{ min: YEAR_MIN, max: YEAR_MAX }}
                                error={closingFyError}
                                helperText={closingFyError ? `${YEAR_MIN}-${YEAR_MAX}` : ' '}
                                sx={{ width: 160 }}
                            />
                            <Button data-help="leave.accrual.action.carryForward"
                                variant="outlined"
                                size="small"
                                onClick={handleCarryForward}
                                disabled={actionPending || closingFyError}
                            >
                                Carry forward for all employees
                            </Button>
                            <Button data-help="leave.accrual.action.undo"
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={handleUndo}
                                disabled={actionPending || closingFyError}
                            >
                                Undo for all employees
                            </Button>
                        </Stack>

                        <FormControlLabel
                            data-help="leave.accrual.force"
                            control={
                                <Checkbox
                                    checked={force}
                                    onChange={(e) => setForce(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Force recompute (overwrites any carry-forward rows that already exist for this fiscal year)"
                        />
                    </>
                )}
            </Box>
        </Drawer>
    );
};

export default AccrualHistoryDrawer;
